import type { ComponentType } from "react";
import { LeadsPreview } from "./previews/LeadsPreview";
import { TasksPreview } from "./previews/TasksPreview";
import { CommunicationPreview } from "./previews/CommunicationPreview";
import { ShowingsPreview } from "./previews/ShowingsPreview";
import { SyndicationPreview } from "./previews/SyndicationPreview";
import { AnalyticsPreview } from "./previews/AnalyticsPreview";
import { WorkflowsPreview } from "./previews/WorkflowsPreview";
import { EmailPreview, EmailPreviewCompact } from "./previews/EmailPreview";
import { AiLeasingAgentPreview } from "./previews/AiLeasingAgentPreview";
import { CallPreview } from "./previews/CallPreview";

export interface PaywallCopy {
  title: string;
  description: string;
  Preview: ComponentType;
  /**
   * 399×190 teaser for the compact card (`BoomCrmPaywall variant="compact"`,
   * the locked composer tabs). Falls back to `Preview` when absent.
   */
  CompactPreview?: ComponentType;
}

export const PAYWALL_COPY = {
  leads: {
    title: "Unlock leads",
    description:
      "Stop losing leads in spreadsheets and inboxes. Automatically capture and nurture every prospect through a pipeline built to convert.",
    Preview: LeadsPreview
  },
  tasks: {
    title: "Unlock tasks",
    description:
      "Assign work automatically, set due dates, tag teammates, and track what's getting done across your team.",
    Preview: TasksPreview
  },
  connectedEmails: {
    title: "Unlock connected emails",
    description:
      "Link your email address to send & receive emails directly in Boom.",
    Preview: CommunicationPreview
  },
  customEmailTemplates: {
    title: "Unlock custom email templates",
    description:
      "Create your own email templates to send to leads and applicants.",
    Preview: CommunicationPreview
  },
  // Locked Email channel inside the inbox composer (Figma BoomCRM-Paywall
  // 31:23290 "Unlock email communication"): the compact card, anchored to the tab.
  email: {
    title: "Unlock email communication with BoomCRM Pro",
    description:
      "Bring SMS, email, and chat into one unified inbox. Talk to leads and applicants from a single place.",
    Preview: EmailPreview,
    CompactPreview: EmailPreviewCompact
  },
  // Locked Call channel inside the inbox composer (Figma BoomCRM-Paywall
  // 393:4883 / 394:7658). Only Email and Call get the paywall treatment; SMS
  // has no design and is hidden when its flag is off (Viktor, 2026-09-03).
  call: {
    title: "Unlock phone communication with BoomCRM Pro",
    description:
      "Make and receive calls with leads and applicants directly from BoomCRM. Keep all your communication in one place.",
    Preview: CallPreview,
    CompactPreview: CallPreview
  },
  showings: {
    title: "Unlock showings",
    description:
      "Let prospects book guided or self-guided tours that sync with your team's calendars in real time. Automated reminders reduce no-shows and free your leasing agents from scheduling back-and-forth.",
    Preview: ShowingsPreview
  },
  // Shorter showings copy for the Codebox / SentriLock integration hover (Figma
  // 34:24080). Not wired yet: `settings-integration` currently HIDES those rows
  // for a partner without `showings` instead of showing them locked, and
  // switching to the locked treatment is a product call, not a defect fix.
  showingsCodebox: {
    title: "Unlock showings",
    description:
      "Let prospects book tours that sync with your team's calendar in real time. Automated reminders reduce no-shows and free your leasing agents from scheduling back-and-forth.",
    Preview: ShowingsPreview
  },
  // No Figma frame exists for lockboxes or phone numbers — both are granted by
  // the Core plan, so the paywall only appears once an admin revokes them, and
  // the design file predates that path. Copy below is DRAFTED, pending design
  // review; the previews are borrowed from the nearest surface (a lockbox exists
  // to enable a self-guided tour, a provisioned number is the SMS channel).
  lockboxes: {
    title: "Unlock lockboxes",
    description:
      "Hand out one-time access codes that expire on their own, so prospects can tour without a leasing agent meeting them to hand over keys.",
    Preview: ShowingsPreview
  },
  phoneNumbers: {
    title: "Unlock phone numbers",
    description:
      "Give your team a dedicated number for every property, so texts and calls from prospects land in the shared inbox instead of a personal phone.",
    Preview: CommunicationPreview
  },
  // Plan-cap upsells, shown on the locked "Add number" / "Create workflow"
  // buttons. The caps are per-partner entitlements an admin can raise, so the
  // copy is deliberately plan-agnostic about the number ("a limited number of")
  // — Jon's wording, Slack 2026-09-03. No Figma frame.
  phoneNumbersLimit: {
    title: "Unlock more phone numbers",
    description:
      "Your Core plan includes a limited number of phone numbers. Upgrade to BoomCRM Pro to give every property or agent its own line in the shared inbox.",
    Preview: CommunicationPreview
  },
  // Workflows are granted by Core (one workflow); this full-page paywall only
  // appears once an admin revokes the entitlement. Copy DRAFTED.
  workflows: {
    title: "Unlock workflows",
    description:
      "Automate follow-ups, hand-offs and reminders so every lead gets the right message at the right time — without lifting a finger.",
    Preview: WorkflowsPreview
  },
  workflowsLimit: {
    title: "Unlock unlimited workflows",
    description:
      "Your Core plan includes a limited number of workflows. Upgrade to BoomCRM Pro to automate every follow-up, hand-off and reminder across your pipeline.",
    Preview: WorkflowsPreview
  },
  // Syndication is granted by Core; when an admin revokes it the five listing-feed
  // integrations stay in the Add integration list, locked with this card on hover,
  // and their settings pages paywall themselves (CRM-908; Figma 29:14299, 34:24027).
  syndication: {
    title: "Unlock syndication",
    description:
      "Push your listings to Zillow and other listing sources with one click.",
    Preview: SyndicationPreview
  },
  // Listing settings (listing templates) ride the showings entitlement (CRM-908:
  // "the Showings calendar and Listing settings"), granted by Core — the paywall
  // only appears once an admin revokes it. Copy DRAFTED, preview borrowed from
  // syndication (no Figma frame).
  listingSettings: {
    title: "Unlock listing settings",
    description:
      "Configure listing templates, prescreening questions and showing requests for your public listing pages.",
    Preview: SyndicationPreview
  },
  // Settings → Listings page has no entitlement of its own in the CRM-908 spec;
  // until product decides, it follows Listing settings (showings). Copy DRAFTED,
  // preview borrowed.
  listingsPage: {
    title: "Unlock your listings page",
    description:
      "Choose the filters, card fields and property order your public listings page shows to prospects.",
    Preview: SyndicationPreview
  },
  // Pro-only customization of lead sources & stages. The defaults stay visible
  // and usable on Core; only Add / Edit / Delete lock (CRM-759). Copy DRAFTED.
  customizeLeadSourcesStages: {
    title: "Unlock custom lead sources & stages",
    description:
      "Add your own lead sources and shape pipeline stages around the way your team actually works. Default sources and stages stay available on every plan.",
    Preview: LeadsPreview
  },
  // LEGACY ANALYTICS TABS: the old BoomCRM analytics tab locks behind this for
  // partners without the paid `crm_analytics` entitlement. The merged Leasing
  // dashboard has no paid tier, so this goes when the old tabs do (PLT-634).
  analytics: {
    title: "Unlock CRM analytics",
    description:
      "See your full lead pipeline, conversion by stage, and lead sources — so you know exactly where prospects drop off and which channels deliver.",
    Preview: AnalyticsPreview
  },
  // Locked AI Leasing agent card in the add-agent wizard (Figma
  // BoomCRM-Paywall 202:6089).
  aiLeasingAgent: {
    title: "Unlock the AI Leasing agent with BoomCRM Pro",
    description:
      "Let AI qualify prospects, answer questions about your inventory and policies, and schedule showings — 24/7, on every channel.",
    Preview: AiLeasingAgentPreview
  }
} as const satisfies Record<string, PaywallCopy>;

export type PaywallFeature = keyof typeof PAYWALL_COPY;
